/**
 * Nooma dataLayer (GTM) — Prestige payload parity on Horizon.
 * Source of truth: docs/dev-datalayer-migration-plan.md + docs/fixtures/datalayer/
 *
 * Module-first API (`NoomaDL`). Thin `window.*` aliases for Wishlist / complementary / bundle / BIS.
 *
 * Variant id keys (temporary Prestige mix — see `applyVariantIdKey`):
 * most ecommerce items use `item_variant_id`; PDP ATC / BIS / user_data cart use `variant_id`;
 * complementary sends both. End-state: always `item_variant_id`.
 * Restore: docs/dev-datalayer-migration-plan.md §1.3 “Restore playbook” + docs/INTERNAL.md → DataLayer / GTM.
 */
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} DlIntent
 * @property {string} [flag]
 * @property {number} [at]
 */

/**
 * @typedef {object} DlCartLine
 * @property {string|number} [id]
 * @property {string|number} [variant_id]
 * @property {string|number} [product_id]
 * @property {number} [quantity]
 * @property {string} [sku]
 * @property {string} [product_title]
 * @property {string} [title]
 * @property {string} [product_type]
 * @property {string} [variant_title]
 * @property {number} [price]
 * @property {number} [original_price]
 * @property {number} [compare_at_price]
 * @property {number} [final_line_price]
 * @property {number} [line_price]
 * @property {string[]} [variant_options]
 * @property {string[]} [options]
 * @property {Record<string, string>|Array<{name?: string, key?: string, value?: string}>} [properties]
 */

/**
 * @typedef {object} DlEcommerceItem
 * @property {string} [item_list_name]
 * @property {string} [index]
 * @property {string} [item_id]
 * @property {string} [item_name]
 * @property {string} [item_category]
 * @property {string} [price]
 * @property {string} [price_compare]
 * @property {string} [currency]
 * @property {string} [percentage_discount]
 * @property {string} [item_variant]
 * @property {string} [item_product_id]
 * @property {string} [item_variant_id]
 * @property {string} [item_shipping_date]
 * @property {string} [item_color]
 * @property {string} [item_size]
 * @property {string} [quantity]
 * @property {string} [item_complementary]
 * @property {string} [variant_id]
 */

/**
 * @typedef {object} DlListRow
 * @property {string} [item_list_name]
 * @property {string|number} [index]
 * @property {string|number} [item_id]
 * @property {string} [item_name]
 * @property {string} [item_category]
 * @property {string|number} [price]
 * @property {string|number} [price_compare]
 * @property {string|number} [percentage_discount]
 * @property {string} [item_variant]
 * @property {string|number} [item_product_id]
 * @property {string|number} [item_variant_id]
 * @property {string} [item_color]
 * @property {string} [item_size]
 */

const COOKIE_USER_ID = 'datalayer-event-id';
const FLAG_PRODUCT_PAGE = 'Product page';
const FLAG_COLLECTION_PAGE = 'Collection page';
const FLAG_SEARCH_PAGE = 'Search page';
const FLAG_CART_PLUS = 'Cart drawer plus button';
const FLAG_CART_MINUS = 'Cart drawer minus button';
const FLAG_WISHLIST = 'Wislist Page';
const FLAG_COMPLEMENTARY = 'Complementary product';
const FLAG_BUNDLE = 'Bundle product';
const FLAG_SAMPLES = 'Samples drawer';
const LIST_PREDICTIVE = 'Search navigation input';

/** Intent from submit only — ignore if older than this (abandon safety net). */
const INTENT_TTL_MS = 5000;

const SKIP_CART_SOURCES = new Set([
  'dev-complementary-product-picker',
  'dev-bundle-configurator',
  'dev-bundle-cart',
  'dev-samples-drawer',
]);

/** @type {Map<string, string>} */
const ANALYTIC_EVENT_BY_ATTR = new Map([
  ['availability-in-store-accordion-button', 'availability_in_store_accordion_click'],
  ['our-stores-link', 'our_stores_link_click'],
  ['our-stores-footer-link', 'our_stores_footer_link_click'],
]);

/** @type {NoomaDlConfig} */
const config = window.__NOOMA_DL__ ?? {
  currency: '',
  country: '',
  template: '',
  isPro: false,
  customerId: null,
  isLoggedIn: false,
  listName: '',
  shippingDateDefault: 'hidden info',
  page: { title: '', handle: '', ogTitle: '' },
  customer: null,
  cart: { total_price: 0, item_count: 0, items: [] },
  product: null,
  listItems: [],
};

const debug =
  typeof window !== 'undefined' &&
  (window.location.search.includes('dl_debug=1') || window.sessionStorage?.getItem('dl_debug') === '1');

/** @type {DlIntent | null} */
let intent = null;

/** @type {Map<string, number>} */
let cartQtyByVariant = new Map();

/** @type {Map<string, DlCartLine>} */
let cartLineByVariant = new Map();

/** Serial cart:update processor — one in flight, FIFO with drawer ± coalesce. */
let cartUpdateDraining = false;
/** @type {CustomEvent[]} */
const cartUpdateQueue = [];
/** Soft cap; excess drops oldest drawer ± first (ATC / errors kept). */
const CART_UPDATE_QUEUE_MAX = 12;

/**
 * @returns {string}
 */
function createEventId() {
  return (
    Date.now().toString(36) +
    Math.floor(Math.pow(10, 12) + Math.random() * 9 * Math.pow(10, 12)).toString(36) +
    '-' +
    ('uniq' + Math.floor(Math.pow(10, 12) + Math.random() * 9 * Math.pow(10, 12)).toString(36))
  );
}

/**
 * @param {string} name
 * @returns {string}
 */
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  const value = match?.[1];
  return value != null ? decodeURIComponent(value) : '';
}

/**
 * @param {string} name
 * @param {string} value
 * @param {number} days
 */
function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/`;
}

/**
 * @returns {string}
 */
function ensureUserIdCookie() {
  let id = getCookie(COOKIE_USER_ID).replace(/"/g, '');
  if (!id) {
    id = createEventId();
    setCookie(COOKIE_USER_ID, id, 365);
  }
  return id;
}

/**
 * @param {number | string | null | undefined} cents
 * @returns {string}
 */
function moneyString(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return (n / 100).toFixed(2);
}

/**
 * @param {number | string | null | undefined} price
 * @param {number | string | null | undefined} compare
 * @returns {string}
 */
function discountPercentString(price, compare) {
  const p = Number(price);
  const c = Number(compare);
  if (!Number.isFinite(p) || !Number.isFinite(c) || c <= 0 || c <= p) return '0';
  return String(Math.round(((c - p) / c) * 100));
}

/**
 * Prestige color/size rules from variant options.
 * @param {string[] | null | undefined} options
 * @param {{ color?: string, size?: string }} [fallback]
 * @returns {{ color: string, size: string }}
 */
function parseColorSize(options, fallback = {}) {
  const opts = Array.isArray(options) ? options.filter((o) => o != null && o !== '') : [];
  if (opts.length <= 2) {
    return { color: opts[0] || fallback.color || '', size: fallback.size || '' };
  }
  if (opts.length === 3 && String(opts[0]).indexOf('cm') >= 0) {
    return { color: '', size: opts[0] || '' };
  }
  return { color: opts[0] || fallback.color || '', size: fallback.size || '' };
}

/**
 * @param {number | null | undefined} inventoryQty
 * @param {string} [fallback]
 * @returns {string}
 */
function shippingDateFromInventory(inventoryQty, fallback) {
  const qty = Number(inventoryQty);
  if (Number.isFinite(qty) && qty >= 1 && qty > 10) return '2 business days';
  return fallback != null ? fallback : 'hidden info';
}

/**
 * @param {Record<string, unknown>} payload
 */
function push(payload) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
  if (debug) {
    console.info('[NoomaDL]', payload.event, payload);
  }
}

/**
 * @param {Partial<DlIntent>} next
 */
function setIntent(next) {
  intent = { ...(intent || {}), ...next, at: Date.now() };
}

function clearIntent() {
  intent = null;
}

/**
 * @returns {string}
 */
function customerIdValue() {
  return config.customerId != null && config.customerId !== '' ? String(config.customerId) : 'not logged in';
}

/**
 * @param {DlCartLine[]} items
 */
function rememberCart(items) {
  cartQtyByVariant = new Map();
  cartLineByVariant = new Map();
  for (const item of items || []) {
    const vid = String(item.variant_id || item.id || '');
    if (!vid) continue;
    const qty = Number(item.quantity) || 0;
    cartQtyByVariant.set(vid, (cartQtyByVariant.get(vid) || 0) + qty);
    cartLineByVariant.set(vid, item);
  }
}

/**
 * Full cart.js / cart/change.js / cart/update.js shape (not /cart/add.js partial).
 * @param {any} resource
 * @returns {boolean}
 */
function isFullCartResource(resource) {
  return Boolean(
    resource &&
      typeof resource === 'object' &&
      typeof resource.item_count === 'number' &&
      Array.isArray(resource.items)
  );
}

/**
 * @returns {Promise<{ ok: boolean, items: DlCartLine[] }>}
 */
async function fetchCartItems() {
  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return { ok: false, items: [] };
    const cart = await res.json();
    return { ok: true, items: /** @type {DlCartLine[]} */ (cart.items || []) };
  } catch {
    return { ok: false, items: [] };
  }
}

/**
 * Prefer event payload when it is a full cart; otherwise fetch /cart.js.
 * Drawer ± / update.js already ship a full cart — no extra network.
 * @param {any} resource
 * @returns {Promise<{ ok: boolean, items: DlCartLine[] }>}
 */
async function resolveCartItems(resource) {
  if (isFullCartResource(resource)) {
    return { ok: true, items: /** @type {DlCartLine[]} */ (resource.items) };
  }
  return fetchCartItems();
}

/**
 * @param {DlCartLine} line
 * @param {{ quantity?: string | number, shippingDate?: string, color?: string, size?: string }} [overrides]
 * @returns {DlEcommerceItem}
 */
function buildItemFromCartLine(line, overrides = {}) {
  const options = line.variant_options || line.options || [];
  const parsed = parseColorSize(options, {
    color: overrides.color,
    size: overrides.size,
  });
  const price = line.price;
  const compare = line.original_price != null ? line.original_price : line.compare_at_price;

  return {
    item_id: line.sku != null ? String(line.sku).trim() : '',
    item_name: line.product_title || line.title || '',
    item_category: line.product_type || '',
    price: moneyString(price),
    price_compare: moneyString(compare),
    currency: config.currency || '',
    percentage_discount: discountPercentString(price, compare),
    item_variant: line.variant_title || line.title || '',
    item_product_id: line.product_id != null ? String(line.product_id) : '',
    item_variant_id: String(line.variant_id || line.id || ''),
    item_shipping_date: overrides.shippingDate != null ? overrides.shippingDate : '',
    item_color: parsed.color || '',
    item_size: parsed.size || '',
    quantity: overrides.quantity != null ? String(overrides.quantity) : '1',
  };
}

/**
 * @param {object} [variant]
 * @param {{ quantity?: string | number }} [overrides]
 * @returns {DlEcommerceItem | null}
 */
function buildItemFromProductConfig(variant, overrides = {}) {
  const product = config.product;
  if (!product) return null;

  const v = variant || product.variant || {};
  const options = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
  const parsed = parseColorSize(options, {
    color: product.item_color,
    size: product.item_size,
  });
  const price = v.price != null ? v.price : product.variant?.price;
  const compare = v.compare_at_price != null ? v.compare_at_price : product.variant?.compare_at_price;
  const shipping = shippingDateFromInventory(
    v.inventory_quantity != null ? v.inventory_quantity : product.variant?.inventory_quantity,
    product.shipping_date || config.shippingDateDefault
  );

  return {
    item_id: v.sku != null ? String(v.sku).trim() : product.variant?.sku ? String(product.variant.sku).trim() : '',
    item_name: product.title || '',
    item_category: product.type || '',
    price: moneyString(price),
    price_compare: moneyString(compare),
    currency: config.currency || '',
    percentage_discount:
      product.percentage_discount != null
        ? String(Math.round(Number(product.percentage_discount)))
        : discountPercentString(price, compare),
    item_variant: v.name || v.title || product.variant?.title || '',
    item_product_id: product.id != null ? String(product.id) : '',
    item_variant_id: v.id != null ? String(v.id) : product.variant?.id != null ? String(product.variant.id) : '',
    item_shipping_date: shipping,
    item_color: parsed.color || '',
    item_size: parsed.size || '',
    quantity: overrides.quantity != null ? String(overrides.quantity) : '1',
  };
}

/**
 * Prestige used mixed variant-id keys; GTM later approved unifying to `item_variant_id` everywhere (2026-07-20).
 * Temporary (2026-07-27): mirror Prestige so existing GTM tags keep matching. Target end-state = always `item_variant_id`.
 * To restore unified keys: follow docs/dev-datalayer-migration-plan.md §1.3 Restore playbook (delete this helper + flip call sites).
 *
 * Prestige map (from analytics inventory):
 * - `variant_id` only → user_data cart, PDP ATC, back_in_stock
 * - both keys → complementary ATC
 * - `item_variant_id` → view_item, lists, select_item, wishlist, cart±, samples, bundle, upsell, …
 * secret_wish_submit: Horizon event-only (no items) — do not invent ecommerce here.
 *
 * @param {DlEcommerceItem} item
 * @param {'item_variant_id' | 'variant_id' | 'both'} [mode]
 * @returns {DlEcommerceItem}
 */
function applyVariantIdKey(item, mode = 'item_variant_id') {
  const id =
    item.item_variant_id != null && String(item.item_variant_id) !== ''
      ? String(item.item_variant_id)
      : item.variant_id != null
        ? String(item.variant_id)
        : '';

  if (mode === 'variant_id') {
    // Unified target (restore when GTM is ready for item_variant_id everywhere):
    // const { variant_id: _v, ...rest } = item;
    // return { ...rest, item_variant_id: id };
    const { item_variant_id: _drop, ...rest } = item;
    return { ...rest, variant_id: id };
  }

  if (mode === 'both') {
    // Complementary Prestige quirk: both keys present with the same value.
    // Unified target: const { variant_id: _v, ...rest } = item; return { ...rest, item_variant_id: id };
    return { ...item, item_variant_id: id, variant_id: id };
  }

  const { variant_id: _drop, ...rest } = item;
  return { ...rest, item_variant_id: id };
}

/**
 * @param {DlEcommerceItem} item
 * @param {string} flag
 * @param {string} [eventName]
 */
function pushAddOrRemove(item, flag, eventName = 'add_to_cart_nooma') {
  push({
    event: eventName,
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
    add_to_cart_flag: flag,
    ecommerce: {
      items: [item],
    },
  });
}

/**
 * @param {DlListRow} listRow
 * @returns {DlEcommerceItem}
 */
function buildListEcommerceItem(listRow) {
  return {
    item_list_name: listRow.item_list_name || config.listName || '',
    index: listRow.index != null ? String(listRow.index) : '',
    item_id: listRow.item_id != null ? String(listRow.item_id) : '',
    item_name: listRow.item_name || '',
    item_category: listRow.item_category || '',
    price: moneyString(listRow.price),
    price_compare: moneyString(listRow.price_compare),
    currency: config.currency || '',
    percentage_discount:
      listRow.percentage_discount != null
        ? String(Math.round(Number(listRow.percentage_discount)))
        : discountPercentString(listRow.price, listRow.price_compare),
    item_variant: listRow.item_variant || '',
    item_product_id: listRow.item_product_id != null ? String(listRow.item_product_id) : '',
    item_variant_id: listRow.item_variant_id != null ? String(listRow.item_variant_id) : '',
    item_shipping_date: config.shippingDateDefault || 'hidden info',
    item_color: listRow.item_color || '',
    item_size: listRow.item_size || '',
  };
}

function pushUserData() {
  const userId = ensureUserIdCookie();
  const eventId = createEventId();
  const charset = document.querySelector('meta[charset]')?.getAttribute('charset') || 'UTF-8';
  const ogTitle =
    document.querySelector("meta[property='og:title']")?.getAttribute('content') ||
    config.page?.ogTitle ||
    document.title ||
    '';

  // Prestige user_data cart used `variant_id` (not item_variant_id). Keep for GTM parity.
  // Unified target later: item_variant_id on these rows too.
  /** @type {Record<string, unknown>[]} */
  const productsCart = (config.cart?.items || []).map((line) => ({
    id: line.sku != null ? String(line.sku) : '',
    name: line.product_title || '',
    category: line.product_type || '',
    variant: line.variant_title || '',
    price: moneyString(line.price),
    quantity: line.quantity != null ? String(line.quantity) : '',
    list: '',
    product_id: line.product_id != null ? String(line.product_id) : '',
    variant_id: line.variant_id != null ? String(line.variant_id) : '',
    // item_variant_id: line.variant_id != null ? String(line.variant_id) : '',
  }));

  /** @type {Record<string, unknown>} */
  const userProperties = {
    visitor_type: config.isLoggedIn ? 'logged_id' : 'guest',
    session_count: '',
    session_id: '',
    user_id: userId,
  };

  if (config.isLoggedIn && config.customer) {
    const c = config.customer;
    Object.assign(userProperties, {
      ip_address: '',
      customer_id: c.id != null ? String(c.id) : '',
      customer_email: c.email || '',
      customer_first_name: c.first_name || '',
      customer_last_name: c.last_name || '',
      customer_phone: c.phone || '',
      customer_city: c.city || '',
      customer_zip: c.zip || '',
      customer_address_1: c.address1 || '',
      customer_address_2: c.address2 || '',
      customer_country: c.country || '',
      customer_order_count: c.orders_count != null ? String(c.orders_count) : '',
      customer_total_spent: c.total_spent != null ? String(c.total_spent) : '',
      customer_tags: c.tags || '',
    });
  }

  push({
    event: 'user_data',
    event_id: eventId,
    event_time: new Date().toString(),
    cart_total: moneyString(config.cart?.total_price),
    currency: config.currency || '',
    country: config.country || '',
    device: {
      screen_resolution: `${window.screen.availWidth}x${window.screen.availHeight}`,
      encoding: String(charset).toUpperCase(),
      language: String(document.documentElement.lang || '').toUpperCase(),
    },
    page: {
      title: ogTitle,
    },
    user_properties: userProperties,
    ecommerce: {
      currency: config.currency || '',
      items: productsCart,
    },
    marketing: {
      _ga: getCookie('_ga') || '',
      _gid: getCookie('_gid') || '',
      _fbp: getCookie('_fbp') || '',
      _fbc: getCookie('_fbc') || '',
    },
  });
}

/**
 * Lightweight Core Web Vitals → dataLayer (Prestige shape).
 */
function initCoreWebVitals() {
  /**
   * @param {{ name: string, id: string, value: number, delta: number }} metric
   */
  const send = (metric) => {
    push({
      event: 'coreWebVitals',
      webVitalsMeasurement: {
        name: metric.name,
        id: metric.id,
        value: metric.value,
        delta: metric.delta,
        valueRounded: Math.round(metric.value),
        deltaRounded: Math.round(metric.delta),
      },
    });
  };

  /**
   * @param {string} name
   * @returns {string}
   */
  const metricId = (name) => `v4-${Date.now()}-${Math.floor(Math.random() * 9e12 + 1e12)}-${name}`;

  try {
    if ('PerformanceObserver' in window) {
      let lcpValue = 0;
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const last = entries[entries.length - 1];
        if (last) lcpValue = last.startTime;
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

      const reportLcp = () => {
        if (lcpValue > 0) {
          send({ name: 'LCP', id: metricId('LCP'), value: lcpValue, delta: lcpValue });
        }
        lcpObserver.disconnect();
      };
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') reportLcp();
      });
      window.addEventListener('pagehide', reportLcp);

      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const shift = /** @type {{ hadRecentInput?: boolean, value?: number }} */ (entry);
          if (shift.hadRecentInput) continue;
          clsValue += shift.value || 0;
        }
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });

      const reportCls = () => {
        send({ name: 'CLS', id: metricId('CLS'), value: clsValue, delta: clsValue });
        clsObserver.disconnect();
      };
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') reportCls();
      });

      if ('PerformanceEventTiming' in window) {
        let inpValue = 0;
        const inpObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const duration = /** @type {PerformanceEventTiming} */ (entry).duration;
            if (duration > inpValue) inpValue = duration;
          }
        });
        // durationThreshold is valid for event-timing observers; omit from strict PerformanceObserverInit.
        inpObserver.observe(/** @type {any} */ ({ type: 'event', buffered: true, durationThreshold: 40 }));
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden' && inpValue > 0) {
            send({ name: 'INP', id: metricId('INP'), value: inpValue, delta: inpValue });
            inpObserver.disconnect();
          }
        });
      }

      const fidObserver = new PerformanceObserver((list) => {
        const first = list.getEntries()[0];
        if (!first) return;
        const fidEntry = /** @type {PerformanceEventTiming} */ (first);
        const value = fidEntry.processingStart - fidEntry.startTime;
        send({ name: 'FID', id: metricId('FID'), value, delta: value });
        fidObserver.disconnect();
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
    }
  } catch (error) {
    if (debug) console.warn('[NoomaDL] coreWebVitals init failed', error);
  }
}

/**
 * @param {object} [variant]
 */
function pushViewItem(variant) {
  const item = buildItemFromProductConfig(variant);
  if (!item) return;

  const { quantity: _q, ...viewItem } = item;

  push({
    event: 'view_item',
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
    ecommerce: {
      items: [viewItem],
    },
  });
}

function pushViewItemList() {
  const rows = config.listItems || [];
  if (!rows.length) return;

  const items = rows.map((row) => buildListEcommerceItem(row));
  /** @type {Record<string, unknown>} */
  const payload = {
    event: 'view_item_list',
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
    ecommerce: {
      items,
    },
  };

  if (config.template === 'search') {
    payload.filter = {
      flag: '',
      room: '',
      type: '',
      color: '',
      price: '',
      width: '',
      material: '',
    };
  }

  push(payload);
}

/**
 * @param {Event} event
 */
function onVariantUpdate(event) {
  if (config.template !== 'product') return;
  if (config.isPro) return;

  const detail = /** @type {CustomEvent} */ (event).detail;
  const resource = detail?.resource;
  if (!resource || !resource.id) return;

  if (config.product) {
    config.product.variant = {
      ...config.product.variant,
      id: resource.id,
      title: resource.title || resource.name || config.product.variant?.title,
      name: resource.name || resource.title || config.product.variant?.name,
      sku: resource.sku != null ? resource.sku : config.product.variant?.sku,
      price: resource.price != null ? resource.price : config.product.variant?.price,
      compare_at_price:
        resource.compare_at_price != null ? resource.compare_at_price : config.product.variant?.compare_at_price,
      option1: resource.option1,
      option2: resource.option2,
      option3: resource.option3,
      options: resource.options || [resource.option1, resource.option2, resource.option3].filter(Boolean),
      inventory_quantity:
        resource.inventory_quantity != null
          ? resource.inventory_quantity
          : config.product.variant?.inventory_quantity,
    };
    const parsed = parseColorSize(config.product.variant.options, {
      color: config.product.item_color,
      size: config.product.item_size,
    });
    config.product.item_color = parsed.color;
    config.product.item_size = parsed.size;
  }

  pushViewItem(resource);
}

/**
 * Fresh intent only (set on form submit). Stale intent after abandon is ignored.
 * @returns {string}
 */
function intentFlag() {
  if (!intent?.flag) return '';
  const age = Date.now() - (Number(intent.at) || 0);
  if (age > INTENT_TTL_MS) {
    clearIntent();
    return '';
  }
  return intent.flag;
}

/**
 * Template default when no explicit data-dl-flag / intent (not PDP).
 * @returns {string}
 */
function templateAddFlag() {
  if (config.template === 'product') return FLAG_PRODUCT_PAGE;
  if (config.template === 'collection') return FLAG_COLLECTION_PAGE;
  if (config.template === 'search') return FLAG_SEARCH_PAGE;
  return '';
}

/**
 * Resolve ATC flag from intent, markup, or template defaults.
 * @param {Event} event
 * @returns {string}
 */
function resolveAddFlag(event) {
  const fromIntent = intentFlag();
  if (fromIntent) return fromIntent;

  const target = /** @type {EventTarget | null} */ (event.target);
  if (target instanceof Element) {
    const flagged = target.closest('[data-dl-flag]');
    if (flagged instanceof HTMLElement && flagged.dataset.dlFlag) {
      return flagged.dataset.dlFlag;
    }
  }

  const source = /** @type {CustomEvent} */ (event).detail?.data?.source || '';
  if (source === 'cart-items-component') return FLAG_CART_PLUS;

  return templateAddFlag();
}

/**
 * Sync snapshot from a full cart source (event payload or /cart.js).
 * @param {any} [resource]
 * @returns {Promise<boolean>} whether snapshot was updated
 */
async function refreshCartSnapshot(resource) {
  const resolved = await resolveCartItems(resource);
  if (!resolved.ok) return false;
  rememberCart(resolved.items);
  return true;
}

/**
 * @param {CustomEvent} event
 * @returns {{ source: string, didError: boolean }}
 */
function cartUpdateMeta(event) {
  const data = event.detail?.data || {};
  return {
    source: typeof data.source === 'string' ? data.source : '',
    didError: Boolean(data.didError),
  };
}

/**
 * Enqueue cart:update. Rapid drawer ± collapses to the latest full cart (net qty delta).
 * ATC / SKIP / error events stay ordered — flags must not merge.
 * @param {CustomEvent} event
 */
function enqueueCartUpdate(event) {
  const meta = cartUpdateMeta(event);

  if (!meta.didError && meta.source === 'cart-items-component' && cartUpdateQueue.length) {
    const last = cartUpdateQueue[cartUpdateQueue.length - 1];
    if (last) {
      const lastMeta = cartUpdateMeta(last);
      if (!lastMeta.didError && lastMeta.source === 'cart-items-component') {
        cartUpdateQueue[cartUpdateQueue.length - 1] = event;
        return;
      }
    }
  }

  cartUpdateQueue.push(event);

  while (cartUpdateQueue.length > CART_UPDATE_QUEUE_MAX) {
    const dropIdx = cartUpdateQueue.findIndex((queued) => {
      const q = cartUpdateMeta(queued);
      return !q.didError && q.source === 'cart-items-component';
    });
    if (dropIdx >= 0) {
      cartUpdateQueue.splice(dropIdx, 1);
      continue;
    }
    cartUpdateQueue.shift();
  }
}

/**
 * Emit add/remove deltas between remembered snapshot and `items`.
 * @param {DlCartLine[]} items
 * @param {CustomEvent} event
 * @param {string} source
 */
function emitCartQtyDeltas(items, event, source) {
  /** @type {Map<string, number>} */
  const nextQty = new Map();
  /** @type {Map<string, DlCartLine>} */
  const nextLines = new Map();
  for (const item of items) {
    const vid = String(item.variant_id || item.id || '');
    if (!vid) continue;
    const qty = Number(item.quantity) || 0;
    nextQty.set(vid, (nextQty.get(vid) || 0) + qty);
    nextLines.set(vid, item);
  }

  const flag = resolveAddFlag(event);
  const isCartItemsSource = source === 'cart-items-component';
  const allVariantIds = new Set([...cartQtyByVariant.keys(), ...nextQty.keys()]);

  for (const vid of allVariantIds) {
    const prev = cartQtyByVariant.get(vid) || 0;
    const next = nextQty.get(vid) || 0;
    const delta = next - prev;
    if (delta === 0) continue;

    const line = nextLines.get(vid) || cartLineByVariant.get(vid);
    if (!line) continue;

    if (delta > 0) {
      let addFlag = flag;
      if (isCartItemsSource) {
        addFlag = FLAG_CART_PLUS;
      } else if (!addFlag) {
        addFlag = templateAddFlag();
      }

      if (!addFlag || addFlag === FLAG_BUNDLE) continue;

      const item = buildItemFromCartLine(line, {
        quantity: String(delta),
        shippingDate:
          addFlag === FLAG_PRODUCT_PAGE
            ? config.product?.shipping_date || config.shippingDateDefault || 'hidden info'
            : '',
      });

      if (addFlag === FLAG_PRODUCT_PAGE && config.product) {
        // Prefer Liquid/product compare-at + discount (cart.js often collapses original_price to sale price).
        const fromConfig = buildItemFromProductConfig(
          {
            id: line.variant_id || line.id,
            sku: line.sku || config.product.variant?.sku,
            title: line.variant_title || config.product.variant?.title,
            name: line.variant_title || config.product.variant?.name,
            price: line.price != null ? line.price : config.product.variant?.price,
            compare_at_price: config.product.variant?.compare_at_price,
            options: line.variant_options || line.options || config.product.variant?.options,
            inventory_quantity: config.product.variant?.inventory_quantity,
          },
          { quantity: String(delta) }
        );
        if (fromConfig) {
          fromConfig.item_id = item.item_id || fromConfig.item_id;
          fromConfig.item_variant_id = item.item_variant_id || fromConfig.item_variant_id;
          fromConfig.item_name = item.item_name || fromConfig.item_name;
          fromConfig.item_category = item.item_category || fromConfig.item_category;
          const parsed = parseColorSize(line.variant_options || line.options, {
            color: fromConfig.item_color,
            size: fromConfig.item_size,
          });
          fromConfig.item_color = parsed.color;
          fromConfig.item_size = parsed.size;
          // Unified target: pushAddOrRemove(fromConfig, FLAG_PRODUCT_PAGE);
          // Prestige PDP ATC (productHandleAddToCart) used `variant_id`, not `item_variant_id`.
          pushAddOrRemove(applyVariantIdKey(fromConfig, 'variant_id'), FLAG_PRODUCT_PAGE);
        } else {
          // Unified target: pushAddOrRemove(item, FLAG_PRODUCT_PAGE);
          pushAddOrRemove(applyVariantIdKey(item, 'variant_id'), FLAG_PRODUCT_PAGE);
        }
      } else {
        // Cart ± / upsell / STL: use line compare when present; keep empty shipping for drawer ±
        pushAddOrRemove(item, addFlag);
      }
    } else {
      const removeFlag = isCartItemsSource || flag === FLAG_CART_MINUS ? FLAG_CART_MINUS : flag || FLAG_CART_MINUS;
      const item = buildItemFromCartLine(line, {
        quantity: String(Math.abs(delta)),
        shippingDate: '',
      });
      pushAddOrRemove(item, removeFlag, 'remove_from_cart');
    }
  }
}

/**
 * Drawer ± must not clear ATC intent (Upsell / PWW can still be in flight).
 * @param {string} source
 */
function clearIntentUnlessDrawerQty(source) {
  if (source === 'cart-items-component') return;
  clearIntent();
}

/**
 * One cart:update — always finishes; never leaves the drain loop mid-flight.
 * @param {CustomEvent} event
 */
async function processCartUpdate(event) {
  const detail = event.detail || {};
  const { source, didError } = cartUpdateMeta(event);

  if (didError) {
    await refreshCartSnapshot(detail.resource);
    clearIntentUnlessDrawerQty(source);
    return;
  }

  const resolved = await resolveCartItems(detail.resource);
  // Failed fetch + no full cart in event: keep prior snapshot (do not trust add.js partials).
  if (!resolved.ok) {
    clearIntentUnlessDrawerQty(source);
    return;
  }

  const items = resolved.items;

  if (SKIP_CART_SOURCES.has(source)) {
    rememberCart(items);
    clearIntent();
    return;
  }

  emitCartQtyDeltas(items, event, source);
  rememberCart(items);
  clearIntentUnlessDrawerQty(source);
}

/**
 * Drain queue serially. Re-checks after unlock so events that arrived during await are not stuck.
 */
async function drainCartUpdateQueue() {
  if (cartUpdateDraining) return;
  cartUpdateDraining = true;
  try {
    while (cartUpdateQueue.length) {
      const event = cartUpdateQueue.shift();
      if (!event) break;
      try {
        await processCartUpdate(event);
      } catch (error) {
        if (debug) console.warn('[NoomaDL] cart:update failed', error);
        clearIntent();
      }
    }
  } finally {
    cartUpdateDraining = false;
    if (cartUpdateQueue.length) {
      void drainCartUpdateQueue();
    }
  }
}

/**
 * @param {CustomEvent} event
 */
function onCartUpdate(event) {
  enqueueCartUpdate(event);
  void drainCartUpdateQueue();
}

/**
 * Capture ATC source flag on real form submit only (not pointerdown / drawer open).
 *
 * Safe abandon paths (no sticky flag → PDP ATC stays Product page):
 * - Samples: no `data-dl-flag` on drawer; ATC goes through `samplesHandleAddToCart` (SKIP source).
 * - Open then close Upsell/PWW/samples without submit: intent never set.
 * - Full page refresh: intent is in-memory only → gone.
 * - Sticky only if submit already fired and cart:update is still in flight (TTL 5s safety net).
 *
 * @param {Event} event
 */
function captureFlagFromEvent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  // Samples must not set intent — ATC uses samplesHandleAddToCart + SKIP_CART_SOURCES.
  if (target.closest('[data-samples-form], [data-add-samples-btn]')) return;

  const flagged = target.closest('[data-dl-flag]');
  if (!(flagged instanceof HTMLElement)) return;

  const flag = flagged.dataset.dlFlag;
  if (flag) setIntent({ flag });
}

/**
 * Collection / search card → select_item
 * @param {MouseEvent} event
 */
function onSelectItemClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.closest('button, [data-quick-add], wishlist-button-collection, .wishlist-collection-button')) return;

  const card =
    target.closest('product-card[data-product-id]') ||
    target.closest('[data-dl-select-item][data-product-id]') ||
    target.closest('.product-grid__item[data-product-id]');
  if (!(card instanceof HTMLElement)) return;

  const productId = card.dataset.productId || card.getAttribute('data-product-id');
  if (!productId) return;

  const link = target.closest('a[href]');
  if (!link && !card.querySelector('a[href]')) return;

  const listRow =
    (config.listItems || []).find((row) => String(row.item_product_id) === String(productId)) || null;

  const parentName =
    card.dataset.parent_name ||
    card.closest('[data-parent_name]')?.getAttribute('data-parent_name') ||
    config.listName ||
    '';

  const counterRaw =
    card.dataset.item_counter ||
    card.closest('[data-item_counter]')?.getAttribute('data-item_counter') ||
    listRow?.index ||
    '1';
  const counter = Number(counterRaw) || 1;
  const index = String(Math.max(0, counter - 1));

  const filters = extractFilters(window.location.href);

  /** @type {DlEcommerceItem} */
  let item;
  if (listRow) {
    item = buildListEcommerceItem(listRow);
    item.item_list_name = parentName || item.item_list_name;
    item.index = index === '0' ? '1' : index;
  } else {
    item = {
      item_list_name: parentName,
      index: index === '0' ? '1' : index,
      item_id: '',
      item_name: '',
      item_category: '',
      price: '',
      price_compare: '',
      currency: config.currency || '',
      percentage_discount: '',
      item_variant: '',
      item_product_id: String(productId),
      item_variant_id: '',
      item_shipping_date: 'hidden info',
      item_color: '',
      item_size: '',
    };
  }

  // Prestige quirk: index 0 becomes "1"
  if (listRow && Number(counter) === 1) {
    item.index = '1';
  }

  push({
    event: 'select_item',
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
    ecommerce: {
      items: [item],
    },
    filter: {
      flag: '',
      room: filters.room,
      type: filters.type,
      color: filters.color,
      price: filters.price,
      width: filters.width,
      material: filters.material,
    },
  });
}

/**
 * @param {string} url
 */
function extractFilters(url) {
  /** @type {Record<string, string[]>} */
  const buckets = {
    'filter.p.m.custom.product_page_room': [],
    'filter.p.m.my_fields.type2': [],
    'filter.p.m.custom.product_page_color': [],
    'filter.p.m.my_fields.price': [],
    'filter.p.m.custom.product_page_width': [],
    'filter.p.m.custom.product_page_material_filters': [],
  };

  try {
    const params = new URL(url).searchParams;
    for (const [key, value] of params.entries()) {
      if (buckets[key]) buckets[key].push(value);
    }
  } catch {
    // ignore
  }

  const room = buckets['filter.p.m.custom.product_page_room'] || [];
  const type = buckets['filter.p.m.my_fields.type2'] || [];
  const color = buckets['filter.p.m.custom.product_page_color'] || [];
  const price = buckets['filter.p.m.my_fields.price'] || [];
  const width = buckets['filter.p.m.custom.product_page_width'] || [];
  const material = buckets['filter.p.m.custom.product_page_material_filters'] || [];

  return {
    room: room.length ? room : '',
    type: type.length ? type : '',
    color: color.length ? color : '',
    price: price.length ? price : '',
    width: width.length ? width : '',
    material: material.length ? material : '',
  };
}

/**
 * Wishlist King / app payload helpers
 * @param {any} data
 * @param {'add' | 'remove' | 'atc'} mode
 */
function handleWishlistPayload(data, mode) {
  const root = data?.data || data || {};
  const product = root.product || {};
  const variant = root.variant || product.variants?.[0] || {};
  const options = product.options || [];
  const optionValues = options[0]?.values || [];
  const variantOptions = [variant.option1, variant.option2, variant.option3].filter((v) => v != null);

  let color = '';
  let size = '';
  if (options.length <= 2) {
    color = optionValues[0] || variant.option1 || '';
  } else if (options.length === 3 && String(variant.option1 || '').indexOf('cm') >= 0) {
    color = '';
    size = variant.option1 || '';
  } else {
    color = optionValues[0] || variant.option1 || '';
  }

  const price = variant.price != null ? variant.price : product.variants?.[0]?.price;
  const compare =
    variant.compare_at_price != null
      ? variant.compare_at_price
      : product.compare_at_price_min != null
        ? product.compare_at_price_min
        : product.variants?.[0]?.compare_at_price;

  const item = {
    item_id: String(variant.sku || product.variants?.[0]?.sku || ''),
    item_name: String(product.title || ''),
    item_category: String(product.type || ''),
    price: moneyString(price),
    price_compare: moneyString(compare),
    currency: config.currency || '',
    percentage_discount: discountPercentString(price, compare),
    item_variant: String(variant.title || product.variants?.[0]?.title || ''),
    item_product_id: product.id != null ? String(product.id) : '',
    item_variant_id: variant.id != null ? String(variant.id) : product.variants?.[0]?.id != null ? String(product.variants[0].id) : '',
    item_shipping_date: config.shippingDateDefault || 'hidden info',
    item_color: color,
    item_size: size,
    quantity: '1',
  };

  if (mode === 'atc') {
    pushAddOrRemove(item, FLAG_WISHLIST);
    return;
  }

  push({
    event: mode === 'add' ? 'add_to_wishlist' : 'remove_from_wishlist',
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
    add_to_cart_flag: config.template || '',
    ecommerce: {
      items: [item],
    },
  });
}

/**
 * @param {any[]} cartLineItems
 * @param {'add' | 'remove' | 'qty_up'} mode
 * @param {string} [bundleTitle]
 */
function handleBundlePayload(cartLineItems, mode, bundleTitle = '') {
  const items = (cartLineItems || []).map((item) => {
    const built = buildItemFromCartLine(item, {
      quantity: item.quantity != null ? String(item.quantity) : '1',
      shippingDate: '',
    });
    return {
      ...built,
      item_complementary: bundleLineIsComplementary(item) ? '1' : '0',
    };
  });

  const valueCents = bundleLineItemsValueCents(cartLineItems);
  const eventName = mode === 'remove' ? 'remove_from_cart' : 'add_to_cart_nooma';

  push({
    event: eventName,
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
    add_to_cart_flag: FLAG_BUNDLE,
    bundle_title: bundleTitle || '',
    ecommerce: {
      currency: config.currency || '',
      value: (Math.round(valueCents) / 100).toFixed(2),
      bundle_discount_percent: bundleDiscountPercentString(cartLineItems),
      items,
    },
  });
}

/**
 * @param {any} item
 * @returns {boolean}
 */
function bundleLineIsComplementary(item) {
  const props = item?.properties;
  if (!props) return false;
  if (Array.isArray(props)) {
    for (const p of props) {
      const n = p?.name != null ? p.name : p?.key;
      if (n === '_bundle_complementary' && String(p.value) === '1') return true;
    }
    return false;
  }
  return String(props._bundle_complementary) === '1';
}

/**
 * @param {any[]} lineItems
 * @returns {number}
 */
function bundleLineItemsValueCents(lineItems) {
  let sum = 0;
  for (const item of lineItems || []) {
    let cents = item.final_line_price != null ? Number(item.final_line_price) : NaN;
    if (!Number.isNaN(cents)) {
      sum += cents;
      continue;
    }
    cents = item.line_price != null ? Number(item.line_price) : NaN;
    if (!Number.isNaN(cents)) {
      sum += cents;
      continue;
    }
    const p = Number(item.price);
    const q = Number(item.quantity) || 1;
    if (!Number.isNaN(p)) sum += p * q;
  }
  return sum;
}

/**
 * @param {any[]} lineItems
 * @returns {string}
 */
function bundleDiscountPercentString(lineItems) {
  const PROP = '_bundle_discount_percent';
  for (const item of lineItems || []) {
    const props = item.properties;
    if (!props) continue;
    let raw = null;
    if (Array.isArray(props)) {
      for (const p of props) {
        const n = p?.name != null ? p.name : p?.key;
        if (n === PROP && p.value != null && p.value !== '') {
          raw = p.value;
          break;
        }
      }
    } else if (typeof props === 'object') {
      raw = props[PROP];
    }
    if (raw != null && String(raw).trim() !== '') return String(raw).trim();
  }
  return '0';
}

/**
 * Normalize /cart/add.js response into line items.
 * @param {any} response
 * @returns {any[]}
 */
function normalizeCartAddItems(response) {
  if (!response) return [];
  if (Array.isArray(response.items)) return response.items;
  if (Array.isArray(response)) return response;
  if (response.variant_id != null || response.id != null) return [response];
  return [];
}

/**
 * @param {any} detail
 */
function handleComplementaryAdd(detail) {
  const itemsFromResponse = normalizeCartAddItems(detail?.response);
  if (itemsFromResponse.length) {
    const items = itemsFromResponse.map((item) => {
      const built = buildItemFromCartLine(item, {
        quantity: item.quantity != null ? String(item.quantity) : '1',
        shippingDate: '',
      });
      // Unified target: return { ...built, item_complementary: '1' };
      // Prestige complementary ATC sent both `item_variant_id` and `variant_id`.
      return applyVariantIdKey(
        {
          ...built,
          item_complementary: '1',
        },
        'both'
      );
    });
    const valueCents = bundleLineItemsValueCents(itemsFromResponse);
    push({
      event: 'add_to_cart_nooma',
      event_id: createEventId(),
      customer_id: customerIdValue(),
      event_time: new Date().toString(),
      add_to_cart_flag: FLAG_COMPLEMENTARY,
      ecommerce: {
        currency: config.currency || '',
        value: (Math.round(valueCents) / 100).toFixed(2),
        items,
      },
    });
    return;
  }

  const fallbackVid = detail?.variantId != null ? String(detail.variantId) : '';
  push({
    event: 'add_to_cart_nooma',
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
    add_to_cart_flag: FLAG_COMPLEMENTARY,
    ecommerce: {
      items: [
        // Unified target: { item_variant_id: fallbackVid, item_complementary: '1', quantity: '1', currency: … }
        applyVariantIdKey(
          {
            item_variant_id: fallbackVid,
            item_complementary: '1',
            quantity: '1',
            currency: config.currency || '',
          },
          'both'
        ),
      ],
    },
  });
}

/**
 * @param {{ response?: any, checkboxes?: HTMLInputElement[] }} detail
 */
function handleSamplesAdd(detail) {
  const fromResponse = normalizeCartAddItems(detail?.response);
  /** @type {DlEcommerceItem[]} */
  let items;

  if (fromResponse.length) {
    items = fromResponse.map((item) =>
      buildItemFromCartLine(item, {
        quantity: item.quantity != null ? String(item.quantity) : '1',
        shippingDate: '',
      })
    );
  } else {
    items = (detail?.checkboxes || []).map((checkbox) => {
      const options = checkbox.dataset.variantTitle ? [checkbox.dataset.variantTitle] : [];
      const parsed = parseColorSize(options, {});
      return {
        item_id: checkbox.dataset.variantSku || '',
        item_name: checkbox.dataset.productTitle || '',
        item_category: checkbox.dataset.productType || '',
        price: '',
        price_compare: '',
        currency: config.currency || '',
        percentage_discount: '',
        item_variant: checkbox.dataset.variantTitle || '',
        item_product_id: checkbox.dataset.productId || '',
        item_variant_id: checkbox.dataset.variantId || checkbox.value || '',
        item_shipping_date: '',
        item_color: parsed.color || '',
        item_size: parsed.size || '',
        quantity: '1',
      };
    });
  }

  if (!items.length) return;

  push({
    event: 'add_to_cart_nooma',
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
    add_to_cart_flag: FLAG_SAMPLES,
    ecommerce: {
      currency: config.currency || '',
      items,
    },
  });
}

/**
 * @param {string} [formId]
 */
function pushSubscribe(formId) {
  push({
    event: 'subscribe',
    event_id: createEventId(),
    event_time: new Date().toString(),
    subscribe_flag: formId || '',
    subscribe_status: 'sent',
    cart_total: moneyString(config.cart?.total_price),
    currency: config.currency || '',
  });
}

function pushSecretWishSubmit() {
  push({
    event: 'secret_wish_submit',
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
  });
}

/**
 * Extract product handles from predictive search result cards.
 * @param {Element} container
 * @returns {string[]}
 */
function predictiveHandlesFromContainer(container) {
  /** @type {string[]} */
  const handles = [];
  const seen = new Set();
  for (const link of container.querySelectorAll(
    '.predictive-search-results__card--product a[href*="/products/"]'
  )) {
    if (!(link instanceof HTMLAnchorElement)) continue;
    try {
      const url = new URL(link.href, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('products');
      const handle = idx >= 0 ? parts[idx + 1] : '';
      if (!handle || seen.has(handle)) continue;
      seen.add(handle);
      handles.push(handle);
    } catch {
      // ignore
    }
  }
  return handles;
}

/**
 * @param {string} handle
 * @returns {Promise<any | null>}
 */
async function fetchProductJson(handle) {
  const root = window.Shopify?.routes?.root || '/';
  try {
    const res = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * @param {any} product
 * @param {number} index1Based
 * @returns {DlEcommerceItem}
 */
function buildItemFromProductJs(product, index1Based) {
  const variant = product?.variants?.[0] || {};
  const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
  const parsed = parseColorSize(options, {});
  const price = variant.price;
  const compare = variant.compare_at_price;
  return {
    item_list_name: LIST_PREDICTIVE,
    index: String(index1Based),
    item_id: variant.sku != null ? String(variant.sku).trim() : '',
    item_name: product?.title || '',
    item_category: product?.type || product?.product_type || '',
    price: moneyString(price),
    price_compare: moneyString(compare),
    currency: config.currency || '',
    percentage_discount: discountPercentString(price, compare),
    item_variant: variant.title || '',
    item_product_id: product?.id != null ? String(product.id) : '',
    item_variant_id: variant.id != null ? String(variant.id) : '',
    item_shipping_date: config.shippingDateDefault || 'hidden info',
    item_color: parsed.color || '',
    item_size: parsed.size || '',
    quantity: '1',
  };
}

/**
 * @param {CustomEvent} event
 */
async function onPredictiveSearchResults(event) {
  const container = event.detail?.container;
  if (!(container instanceof Element)) return;

  const handles = predictiveHandlesFromContainer(container);
  if (!handles.length) return;

  const products = await Promise.all(handles.map((handle) => fetchProductJson(handle)));
  /** @type {DlEcommerceItem[]} */
  const items = [];
  for (let i = 0; i < products.length; i++) {
    const product = products[i];
    if (!product) continue;
    items.push(buildItemFromProductJs(product, items.length + 1));
  }
  if (!items.length) return;

  push({
    event: 'view_item_list',
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
    ecommerce: {
      items,
    },
  });
}

/**
 * Prestige parity: thank-you page after B2B form (referrer /pages/noo-ma-pro).
 */
function maybePushB2bThankYouLead() {
  const path = String(config.page?.handle || window.location.pathname || '');
  const isThankYou = /thank-you/i.test(path) || Boolean(document.querySelector('[data-dl-b2b-thank-you]'));
  if (!isThankYou) return;

  let referrerPath = '';
  try {
    referrerPath = document.referrer ? new URL(document.referrer).pathname : '';
  } catch {
    referrerPath = '';
  }
  if (!referrerPath.includes('/pages/noo-ma-pro')) return;

  const guardKey = 'nooma_b2b_form_lead';
  try {
    if (sessionStorage.getItem(guardKey)) return;
    sessionStorage.setItem(guardKey, '1');
  } catch {
    // sessionStorage blocked — still fire once this page load
  }

  push({
    event: 'custom_b2b_form_lead',
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
  });
}

/**
 * @param {MouseEvent} event
 */
function onAnalyticClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const analyticEl = target.closest('[data-analytic]');
  if (analyticEl instanceof HTMLElement && analyticEl.dataset.analytic) {
    const eventName = ANALYTIC_EVENT_BY_ATTR.get(analyticEl.dataset.analytic);
    if (eventName) {
      push({
        event: eventName,
        event_id: createEventId(),
        customer_id: customerIdValue(),
        event_time: new Date().toString(),
      });
      return;
    }
  }

  const storesLink = target.closest('a[href*="/pages/stores"]');
  if (!(storesLink instanceof HTMLAnchorElement)) return;

  const inFooter = Boolean(storesLink.closest('footer, [data-section-type="footer"], .shopify-section-group-footer-group'));
  push({
    event: inFooter ? 'our_stores_footer_link_click' : 'our_stores_link_click',
    event_id: createEventId(),
    customer_id: customerIdValue(),
    event_time: new Date().toString(),
  });
}

function installWindowAliases() {
  window.customHandleAddToWishList = (data) => handleWishlistPayload(data, 'add');
  window.customHandleRemoveFromWishList = (data) => handleWishlistPayload(data, 'remove');
  window.customHandleAddToCartFromWishlist = (data) => handleWishlistPayload(data, 'atc');

  window.complementaryProductHandleAddToCart = (detail) => handleComplementaryAdd(detail);
  window.samplesHandleAddToCart = (detail) => handleSamplesAdd(detail);

  window.bundleHandleAddToCart = (items, title) => handleBundlePayload(items, 'add', title);
  window.bundleHandleRemoveFromCart = (items, title) => handleBundlePayload(items, 'remove', title);
  window.bundleHandleCartQtyUp = (items, title) => handleBundlePayload(items, 'qty_up', title);

  window.customHandleSubscribe = (formId) => pushSubscribe(formId);
  window._klaviyoNewsletterForm = () => pushSubscribe('klaviyo-newsletter');
  window.customHandleSecretWish = () => pushSecretWishSubmit();

  window._backInStockForm = () => {
    const item = buildItemFromProductConfig(undefined, { quantity: '1' });
    if (!item) return;
    push({
      event: 'back_in_stock',
      event_id: createEventId(),
      customer_id: customerIdValue(),
      event_time: new Date().toString(),
      back_in_stock_flag: config.product?.coming_soon ? 'Coming Soon' : 'Sold Out',
      ecommerce: {
        // Unified target: items: [item]  // item_variant_id
        // Prestige productHandleBackInStock used `variant_id`.
        items: [applyVariantIdKey(item, 'variant_id')],
      },
    });
  };
}

function bindListeners() {
  document.addEventListener(ThemeEvents.cartUpdate, /** @type {EventListener} */ (onCartUpdate));
  document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate);
  document.addEventListener('submit', captureFlagFromEvent, true);
  document.addEventListener('click', onSelectItemClick, true);
  document.addEventListener('click', onAnalyticClick, true);
  document.addEventListener(
    'nooma:predictive-search-results',
    /** @type {EventListener} */ (/** @type {unknown} */ (onPredictiveSearchResults))
  );
}

function init() {
  rememberCart(/** @type {DlCartLine[]} */ (config.cart?.items || []));
  installWindowAliases();
  bindListeners();

  pushUserData();
  initCoreWebVitals();
  maybePushB2bThankYouLead();

  if (config.template === 'product') {
    pushViewItem();
  } else if (config.template === 'collection' || config.template === 'search') {
    pushViewItemList();
  }

  window.NoomaDL = {
    setIntent,
    clearIntent,
    push,
    createEventId,
    debug: () => debug,
  };

  if (debug) console.info('[NoomaDL] initialized', { template: config.template, config });
}

init();

export { createEventId, setIntent, clearIntent, push };
